import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { EmptyState } from '@siliconx/shared/ui/state';
import { Boxes } from 'lucide-react';
import type {
  AgingBucket,
  InventoryMarginGroup,
  InventoryMarginResponse,
  InventoryMarginSummary,
} from '@siliconx/shared';
import { api, throwIfNotOk } from '../lib/api';
import { formatUsd } from '../lib/format';
import { qk } from '../lib/queryKeys';

/**
 * SIL-2535 - Inventory & Margin (reseller pillar, first screen).
 * `GET /api/client/inventory-margin`: per-unit cost basis vs current FMV,
 * days held, aging buckets, rollups by class / lot / team / site. Cost comes
 * from the recorded purchase price only; a unit without cost or FMV shows no
 * margin rather than a partial one. Gated by the `inventoryMargin` org
 * feature (reseller only); the API gate is the enforcement boundary.
 */
export const Route = createFileRoute('/inventory-margin')({
  component: InventoryMarginPage,
});

type Dimension = 'class' | 'lot' | 'team' | 'site';
const DIMENSIONS: { key: Dimension; label: string }[] = [
  { key: 'class', label: 'Class' },
  { key: 'lot', label: 'Lot' },
  { key: 'team', label: 'Team' },
  { key: 'site', label: 'Site' },
];

const BUCKET_LABEL: Record<AgingBucket, string> = {
  '0-30': '0-30 days',
  '31-60': '31-60 days',
  '61-90': '61-90 days',
  '90+': '90+ days',
  unknown: 'No date',
};

function InventoryMarginPage() {
  const [bucket, setBucket] = useState<AgingBucket | null>(null);
  const [dimension, setDimension] = useState<Dimension>('class');

  const query = useQuery<InventoryMarginResponse>({
    queryKey: qk.inventoryMargin.report(bucket),
    queryFn: async () => {
      const res = await api.api.client['inventory-margin'].$get({
        query: { limit: '100', ...(bucket ? { bucket } : {}) },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as unknown as InventoryMarginResponse;
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inventory & Margin"
        subtitle="Cost basis, current FMV, unrealized margin and days held for the hardware you hold."
        pinTo="/inventory-margin"
      />
      <QueryBoundary query={query} errorTitle="Couldn't load inventory margin.">
        {(data) =>
          data.summary.units === 0 ? (
            <EmptyState
              icon={Boxes}
              title="No inventory yet"
              description="Add assets with a purchase price to see cost basis, margin and aging here."
            />
          ) : (
            <div className="flex flex-col gap-5">
              <SummaryCards summary={data.summary} />
              <AgingCard data={data} active={bucket} onSelect={setBucket} />
              <RollupCard data={data} dimension={dimension} onDimension={setDimension} />
              <UnitsCard data={data} bucket={bucket} />
            </div>
          )
        }
      </QueryBoundary>
    </div>
  );
}

function pct(v: number | null): string {
  return v === null ? '-' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
}

function MarginCell({ usd, pctValue }: { usd: number | null; pctValue: number | null }) {
  if (usd === null) return <span className="tabular-nums text-muted-foreground">-</span>;
  return (
    <span className={`tabular-nums ${usd >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
      {usd >= 0 ? '+' : ''}
      {formatUsd(usd)} ({pct(pctValue)})
    </span>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Card>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 text-xl font-semibold text-foreground">{children}</div>
    </Card>
  );
}

function SummaryCards({ summary }: { summary: InventoryMarginSummary }) {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <Stat label="Units held">{summary.units.toLocaleString()}</Stat>
      <Stat label="Cost basis">{formatUsd(summary.costBasisUsd)}</Stat>
      <Stat label="Current FMV">{formatUsd(summary.fmvUsd)}</Stat>
      <Stat label="Unrealized margin">
        <MarginCell usd={summary.unrealizedMarginUsd} pctValue={summary.unrealizedMarginPct} />
        <div className="mt-1 text-xs font-normal text-muted-foreground">
          {summary.matchedUnits.toLocaleString()} of {summary.units.toLocaleString()} units have both cost and FMV
        </div>
      </Stat>
    </div>
  );
}

function AgingCard({
  data,
  active,
  onSelect,
}: {
  data: InventoryMarginResponse;
  active: AgingBucket | null;
  onSelect: (b: AgingBucket | null) => void;
}) {
  return (
    <Card>
      <PanelTitle title="Aging" description="Days held, from in-service date (purchase date if not in service). Select a bucket to filter the unit list." />
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        {data.buckets.map((b) => (
          <button
            key={b.bucket}
            type="button"
            onClick={() => onSelect(active === b.bucket ? null : b.bucket)}
            aria-pressed={active === b.bucket}
            className={`rounded-md border px-3 py-2 text-left text-sm ${
              active === b.bucket ? 'border-brand-500 bg-brand-50' : 'border-border bg-card hover:bg-muted'
            }`}
          >
            <div className="text-xs font-medium text-muted-foreground">{BUCKET_LABEL[b.bucket]}</div>
            <div className="mt-1 font-semibold tabular-nums text-foreground">{b.units.toLocaleString()} units</div>
            <div className="text-xs tabular-nums text-muted-foreground">{formatUsd(b.costBasisUsd)} cost</div>
          </button>
        ))}
      </div>
    </Card>
  );
}

function RollupCard({
  data,
  dimension,
  onDimension,
}: {
  data: InventoryMarginResponse;
  dimension: Dimension;
  onDimension: (d: Dimension) => void;
}) {
  const groups: InventoryMarginGroup[] = data.rollups[dimension];
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PanelTitle title="Rollup" description="Margin covers only units with both cost and FMV." />
        <div role="group" aria-label="Group by" className="flex gap-1">
          {DIMENSIONS.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => onDimension(d.key)}
              aria-pressed={dimension === d.key}
              className={`rounded-md border px-3 py-1 text-xs font-medium ${
                dimension === d.key ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-border bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="py-2 pr-4">{DIMENSIONS.find((d) => d.key === dimension)?.label}</th>
              <th className="py-2 pr-4 text-right">Units</th>
              <th className="py-2 pr-4 text-right">Cost basis</th>
              <th className="py-2 pr-4 text-right">FMV</th>
              <th className="py-2 pr-4 text-right">Unrealized margin</th>
              <th className="py-2 pr-4 text-right">Avg days held</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {groups.map((g) => (
              <tr key={g.groupId ?? 'unassigned'}>
                <td className="py-3 pr-4 text-foreground">{g.groupLabel}</td>
                <td className="py-3 pr-4 text-right tabular-nums">{g.units.toLocaleString()}</td>
                <td className="py-3 pr-4 text-right tabular-nums">{formatUsd(g.costBasisUsd)}</td>
                <td className="py-3 pr-4 text-right tabular-nums">{formatUsd(g.fmvUsd)}</td>
                <td className="py-3 pr-4 text-right">
                  <MarginCell usd={g.unrealizedMarginUsd} pctValue={g.unrealizedMarginPct} />
                </td>
                <td className="py-3 pr-4 text-right tabular-nums">
                  {g.avgDaysHeld === null ? '-' : Math.round(g.avgDaysHeld)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function UnitsCard({ data, bucket }: { data: InventoryMarginResponse; bucket: AgingBucket | null }) {
  return (
    <Card>
      <PanelTitle
        title={bucket ? `Units - ${BUCKET_LABEL[bucket]}` : 'Units'}
        description="Longest held first."
      />
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="py-2 pr-4">Asset</th>
              <th className="py-2 pr-4">Class</th>
              <th className="py-2 pr-4 text-right">Qty</th>
              <th className="py-2 pr-4 text-right">Cost basis</th>
              <th className="py-2 pr-4 text-right">FMV</th>
              <th className="py-2 pr-4 text-right">Unrealized margin</th>
              <th className="py-2 pr-4 text-right">Days held</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.units.map((u) => (
              <tr key={u.assetId}>
                <td className="py-3 pr-4">
                  <Link
                    to="/assets/$assetId"
                    params={{ assetId: u.assetId }}
                    className="font-mono text-xs text-brand-600 hover:underline"
                  >
                    {u.serialNumber ?? u.assetId.slice(0, 8)}
                  </Link>
                </td>
                <td className="py-3 pr-4 text-foreground">{u.assetClassName ?? u.assetClassId ?? '-'}</td>
                <td className="py-3 pr-4 text-right tabular-nums">{u.quantity}</td>
                <td className="py-3 pr-4 text-right tabular-nums">{u.costBasisUsd === null ? '-' : formatUsd(u.costBasisUsd)}</td>
                <td className="py-3 pr-4 text-right tabular-nums">{u.fmvUsd === null ? '-' : formatUsd(u.fmvUsd)}</td>
                <td className="py-3 pr-4 text-right">
                  <MarginCell usd={u.unrealizedMarginUsd} pctValue={u.unrealizedMarginPct} />
                </td>
                <td className="py-3 pr-4 text-right tabular-nums">{u.daysHeld ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.unitsTruncated && (
        <p className="mt-3 text-xs text-muted-foreground">
          Showing the first {data.units.length} of {data.unitsTotal.toLocaleString()} lines. Filter by aging bucket to narrow.
        </p>
      )}
    </Card>
  );
}
